import { AUTH_URL } from 'src/configs';
import request from 'src/request';

export type Option = { value: string; label: string };

// Reference data backing the admin filter dropdowns. Both lists are small and
// change rarely, so each is fetched once and shared by every caller in the
// session rather than re-requested per mount.

type Currency = { code?: string; name?: string };
type CurrencyPair = { symbol?: string };

const LIMIT = 500;

let currenciesPromise: Promise<Option[]> | null = null;
let pairsPromise: Promise<Option[]> | null = null;

export const fetchCurrencyOptions = () => {
  if (!currenciesPromise) {
    currenciesPromise = request
      .get(`${AUTH_URL}/v1/currencies?page=1&limit=${LIMIT}`)
      .then(({ data }) =>
        ((data?.currencies ?? []) as Currency[])
          .filter((currency) => currency.code)
          .map((currency) => ({
            value: String(currency.code),
            label: currency.name ? `${currency.name} (${currency.code})` : String(currency.code),
          })),
      )
      .catch(() => {
        // Let a later mount retry instead of caching the failure forever.
        currenciesPromise = null;
        return [];
      });
  }
  return currenciesPromise;
};

export const fetchPairOptions = () => {
  if (!pairsPromise) {
    pairsPromise = request
      .get(`${AUTH_URL}/v1/currency-pairs?page=1&limit=${LIMIT}`)
      .then(({ data }) =>
        ((data?.pairs ?? []) as CurrencyPair[])
          .filter((pair) => pair.symbol)
          .map((pair) => ({ value: String(pair.symbol), label: String(pair.symbol) })),
      )
      .catch(() => {
        pairsPromise = null;
        return [];
      });
  }
  return pairsPromise;
};

/** Every country the platform knows, for pickers. `limit=0` = no paging on this endpoint. */
export const fetchCountryOptions = async (): Promise<Option[]> => {
  const response = await request.get(`${AUTH_URL}/v1/countries?limit=0`);
  const countries = (response.data.countries ?? []) as { code: string; name: string }[];
  return countries
    .filter((c) => c.code && c.name)
    .map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
